import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { AiOutlineDelete, AiOutlineEdit } from "react-icons/ai";
import { HiOutlinePlus, HiX, HiEye } from "react-icons/hi";
import { Menu } from "@headlessui/react";
import {
  useDeleteSmsCredentialForTenantMutation,
  useGetSmsCredentialWithLimitOffsetForTenantMutation,
  useUpdateSmsCredentialStatusForTenantMutation,
} from "../../../features/tenant/smsCredential/smsCredentialApi";
import { toast } from "react-hot-toast";
import { hasPermission } from "../../../utils/hasPermission";
import { getVendorName } from "../../../utils/getVendorName";

const providerArray = [
  { id: 1, name: "orbital",   form: { name: ["user_id", "password", "entity_id"], view_name: ["User ID", "Password", "Entity ID"] } },
  { id: 2, name: "smslane",   form: { name: ["api_key", "entity_id"],              view_name: ["API Key", "Entity ID"] } },
  { id: 3, name: "juro",      form: { name: ["token", "entity_id"],                view_name: ["Token", "Entity ID"] } },
  { id: 4, name: "textlocal", form: { name: ["api_key"],                           view_name: ["API Key"] } },
  { id: 5, name: "pinnacle",  form: { name: ["user_id", "password", "entity_id"], view_name: ["User ID", "Password", "Entity ID"] } },
  { id: 6, name: "smartping", form: { name: ["user_id", "password", "entity_id"], view_name: ["User ID", "Password", "Entity ID"] } },
  { id: 7, name: "veup",      form: { name: ["api_key", "entity_id"],              view_name: ["API Key", "Entity ID"] } },
  { id: 8, name: "smscountry",form: { name: ["user_id", "password", "entity_id"], view_name: ["User ID", "Password", "Entity ID"] } },
];

const getProvider = (type) => providerArray.find(p => p.name === type);

// ── Credential view modal ─────────────────────────────────────────────────────
const CredentialModal = ({ row, onClose }) => {
  const provider = getProvider(row.type);
  const [revealed, setRevealed] = useState({});

  const fields = provider
    ? provider.form.name.map((key, i) => ({
        label: provider.form.view_name[i],
        value: Object.values(row.api_credential)[i] ?? "—",
        key,
      }))
    : Object.entries(row.api_credential || {}).map(([k, v]) => ({ label: k, value: v, key: k }));

  const isSensitive = (key) => ["password", "api_key", "token"].includes(key);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(15,23,42,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <div style={{
        background: "#fff", borderRadius: "14px", width: "420px", maxWidth: "92vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden",
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <p style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>Credentials</p>
            <p style={{ margin: 0, fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
              Provider: <strong style={{ textTransform: "capitalize" }}>{row.type}</strong>
              {row.entity_id ? ` · Entity: ${row.entity_id}` : ""}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: "4px", display: "flex" }}>
            <HiX size={18} />
          </button>
        </div>

        {/* Fields */}
        <div style={{ padding: "20px" }}>
          {fields.map(({ label, value, key }) => (
            <div key={key} style={{ marginBottom: "14px" }}>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>
                {label}
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  readOnly
                  type={isSensitive(key) && !revealed[key] ? "password" : "text"}
                  value={value}
                  style={{ flex: 1, padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "13px", background: "#f8fafc", color: "#0f172a", outline: "none", fontFamily: "monospace" }}
                />
                {isSensitive(key) && (
                  <button
                    type="button"
                    onClick={() => setRevealed(p => ({ ...p, [key]: !p[key] }))}
                    style={{ padding: "8px", border: "1px solid #e2e8f0", borderRadius: "6px", background: "#fff", cursor: "pointer", color: "#64748b", display: "flex", flexShrink: 0 }}
                  >
                    <HiEye size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SMSCredential = () => {
  const navigate = useNavigate();
  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme.color_shades;

  const addSMSCredentialPermission     = hasPermission("addSmsSetup");
  const editSMSCredentialPermission    = hasPermission("editSmsSetup");
  const deleteSMSCredentialPermission  = hasPermission("deleteSmsSetup");
  const updateSMSCredentialStatusPermission = hasPermission("updateSmsSetup");
  const viewSMSTemplatesPermission     = hasPermission("viewSmsSetup");

  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationResetToggle, setPaginationResetToggle] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const [getSmsCredentialWithLimitOffsetForTenant, {
    data: listData,
    isLoading: listLoading,
    isError: listIsError,
  }] = useGetSmsCredentialWithLimitOffsetForTenantMutation();

  const [deleteSmsCredentialForTenant, {
    data: deleteData, isError: deleteIsError,
  }] = useDeleteSmsCredentialForTenantMutation();

  const [updateSmsCredentialStatusForTenant, {
    data: statusData, isError: statusIsError,
  }] = useUpdateSmsCredentialStatusForTenantMutation();

  const tenantId = JSON.parse(sessionStorage.getItem("vendorData")).tenant_id;
  const token = () => sessionStorage.getItem("access_token");

  const fetchList = (lim = limit, off = offset) => {
    getSmsCredentialWithLimitOffsetForTenant({ token: token(), tenant_id: tenantId, limit: lim, offset: off });
  };

  useEffect(() => {
    if (!hasPermission("viewSmsSetup")) navigate(`/genefied/${getVendorName()}/`);
    fetchList();
  }, [limit, offset]);

  useEffect(() => {
    if (listIsError) toast.error("Failed to load credentials");
  }, [listIsError]);

  useEffect(() => {
    if (deleteData) { toast.success("Deleted successfully"); fetchList(); }
    if (deleteIsError) toast.error("Delete failed");
  }, [deleteData, deleteIsError]);

  useEffect(() => {
    if (statusData) { toast.success("Status updated"); fetchList(); }
    if (statusIsError) toast.error("Status update failed");
  }, [statusData, statusIsError]);

  const handleEdit = (row) => {
    if (!editSMSCredentialPermission) { toast.error("No permission to edit"); return; }
    const provider = getProvider(row.type);
    navigate("add", { state: { data: row, form: provider, edit: true } });
  };

  const handleDelete = (row) => {
    if (!deleteSMSCredentialPermission) { toast.error("No permission to delete"); return; }
    deleteSmsCredentialForTenant({ token: token(), tenant_id: tenantId, id: row.id });
  };

  const handleStatusChange = (row) => {
    if (!updateSMSCredentialStatusPermission) { toast.error("No permission to change status"); return; }
    updateSmsCredentialStatusForTenant({ token: token(), tenant_id: tenantId, id: Number(row.id) });
  };

  const columns = [
    {
      name: "SNo",
      cell: (row, idx) => offset + idx + 1,
      width: "70px",
    },
    {
      name: "Provider",
      cell: (row) => (
        <span style={{ padding: "3px 10px", background: "#ede9fe", color: "#5b21b6", borderRadius: "20px", fontSize: "12px", fontWeight: 600, textTransform: "capitalize" }}>
          {row.type}
        </span>
      ),
      width: "130px",
    },
    {
      name: "Entity ID",
      cell: (row) => <span style={{ fontFamily: "monospace", fontSize: "12px" }}>{row.entity_id || "—"}</span>,
    },
    {
      name: "Credentials",
      cell: (row) => (
        <button
          onClick={() => setViewRow(row)}
          style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "5px 10px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "12px", color: "#374151", cursor: "pointer" }}
        >
          <HiEye size={13} /> View
        </button>
      ),
      width: "100px",
    },
    {
      name: "Status",
      cell: (row) => {
        const active = row.status === "1";
        return (
          <button
            onClick={() => handleStatusChange(row)}
            style={{ padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600, cursor: "pointer", border: "none", background: active ? "#d1fae5" : "#f1f5f9", color: active ? "#059669" : "#64748b" }}
          >
            {active ? "Active" : "Inactive"}
          </button>
        );
      },
      width: "100px",
    },
    {
      name: "",
      cell: (row) => (
        <div style={{ display: "flex", gap: "6px" }}>
          <button
            onClick={() => handleEdit(row)}
            style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "5px 10px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "12px", color: "#374151", cursor: "pointer" }}
          >
            <AiOutlineEdit size={13} /> Edit
          </button>
          {viewSMSTemplatesPermission && (
            <button
              onClick={() => navigate("sms-templates", { state: row })}
              style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "5px 10px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "6px", fontSize: "12px", color: "#2563eb", cursor: "pointer" }}
            >
              Templates
            </button>
          )}
          <button
            onClick={() => handleDelete(row)}
            style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "5px 10px", background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: "6px", fontSize: "12px", color: "#e11d48", cursor: "pointer" }}
          >
            <AiOutlineDelete size={13} /> Delete
          </button>
        </div>
      ),
      minWidth: "240px",
    },
  ];

  return (
    <>
      {viewRow && <CredentialModal row={viewRow} onClose={() => setViewRow(null)} />}

      <div className="flex w-full bg-gray-200 h-full justify-center">
        <div className="w-full">
          <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
            SMS Credentials
            <LinksComponent />
          </div>
          <div className="p-3">
            <div className="p-3 bg-white shadow rounded-lg">
              <DataTableComponent
                keyField={(row) => row.id}
                columns={columns}
                data={listData?.body?.data}
                totalRows={listData?.body?.total}
                handlePerRowsChange={(n) => setLimit(n)}
                handlePageChange={(p) => { setCurrentPage(p); setOffset((p - 1) * limit); }}
                paginationDefaultPage={currentPage}
                paginationResetDefaultPage={paginationResetToggle}
              />
              {addSMSCredentialPermission && (
                <div className="fixed bottom-0 right-16 w-16 h-16 mr-10 mb-2 cursor-pointer" id="box_btn">
                  <div className="text-right">
                    <Menu className="relative inline-block text-left">
                      <div>
                        <Menu.Button
                          onClick={() => navigate("add")}
                          className="inline-flex justify-center w-12 h-12 rounded-full text-sm font-medium text-white hover:bg-opacity-80 focus:outline-none"
                          style={{ backgroundColor: primaryColor["700"] }}
                        >
                          <HiOutlinePlus className="w-full h-full p-3" />
                        </Menu.Button>
                      </div>
                    </Menu>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SMSCredential;
